import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Feed, Plus } from '@peacecover/icon';
import useChatRoomsQuery from '../../components/api/queries/useChatRoomsQuery';
import StickyHeader from '../components/bar/StickyHeader';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import BettieCreateRoomTicketBottomSheet from '../../components/bottomSheet/bettie/BettieCreateRoomTicketBottomSheet';
import BettieChatCard from './components/BettieChatCard';
import BettieCreditBanner from './components/BettieCreditBanner';
import PopUpBasic from '../components/popup/PopUpBasic';
import BettieFreeChargeSection from './components/BettieFreeChargeSection';
import BettieUnfinishedChatRoomCard from './components/BettieUnfinishedChatRoomCard';
import { BettieGradientColor } from '../../constants/BettieGradientColor';
import FeatureGate from '../../domains/features/ui/components/FeatureGate';
import { FEATURE_KEYS } from '../../domains/features/constants';

const BettieChatListPage = () => {
  const { colorMode } = useColorMode();
  const handleHistoryBack = useHandleHistoryBack();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isCreateTicketBottomSheetOpen, setIsCreateTicketBottomSheetOpen] = useState(false);
  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);
  const [isUnfinishedPopupOpen, setIsUnfinishedPopupOpen] = useState(false);

  const { data, isFetching: isChatRoomsLoading } = useChatRoomsQuery(
    {
      type: 'All',
      tradingPhotoCardId: undefined,
      onlyJoined: true,
      onlyBettie: true
    },
    {
      refetchOnWindowFocus: false
    }
  );
  const { data: bettieBalances, isLoading: isBettieBalancesLoading } = useGetBettieBalances();

  const bettieChatRooms = data?.pages?.flatMap((page) => page.chatRooms) ?? [];

  // 결제 성공 메시지 수신 시 베티 잔액 관련 쿼리 무효화
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string; feature?: string } | undefined;
      if (payload?.type === 'PAYMENT_SUCCESS') {
        queryClient.invalidateQueries(['bettieBalances']);
        queryClient.invalidateQueries(['mySubscription']);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient]);

  const hasBettieChatRoomTicket =
    bettieBalances?.bettieChatRoomTicket?.balance &&
    bettieBalances?.bettieChatRoomTicket?.balance > 0;

  const handleAddChatRoomTicket = () => {
    if (!bettieBalances?.bettieChatRoomTicket) {
      return;
    }
    if (hasBettieChatRoomTicket) {
      setIsUnfinishedPopupOpen(true);
    } else {
      setIsCreateTicketBottomSheetOpen(true);
    }
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+80px)] bg-background-light dark:bg-background-dark">
      <Helmet>
        <title>Bettie</title>
      </Helmet>
      <StickyHeader
        showBackButton={true}
        onClickBackButton={handleHistoryBack}
        rightContent={
          <Feed
            size={24}
            className="text-text-main-lv1"
            onClick={() => navigate('/bettie/purchase-history')}
          />
        }
      />
      <div className="w-full relative flex flex-col items-center justify-center mt-[86px]">
        <div className="w-[240px] h-[105px] rounded-full relative">
          <div
            aria-hidden="true"
            style={BettieGradientColor.titleBackground[colorMode]}
            className="absolute inset-0 rounded-full pointer-events-none"
          />
          <img
            src={`/assets/bettie/bettie_title_${colorMode}.png`}
            alt="Bettie"
            className="w-[158px] h-[74px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10"
          />
        </div>
        <p className="w-full text-center text-text-main-lv2 font-bold text-[17px] leading-[20px]">
          {t('BettieSetting.description')}
        </p>
      </div>
      <div className="px-[24px] mt-[38px]">
        <BettieCreditBanner setIsChargeBottomSheetOpen={setIsChargeBottomSheetOpen} />
        <FeatureGate featureKey={FEATURE_KEYS.CARD_REVIEW} inverted>
          <BettieFreeChargeSection />
        </FeatureGate>
        <div className="mt-[50px]">
          <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv2">
            {t('BettieChatList.myChatRoom')}{' '}
            <span className="text-primary-light dark:text-primary-dark">
              {isChatRoomsLoading ? '-' : bettieChatRooms?.length}
            </span>
          </p>
          <div className="mt-[16px] flex flex-col gap-[10px]">
            {isChatRoomsLoading
              ? Array.from({ length: 2 }).map((_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: skeleton key
                  <BettieChatRoomCardSkeleton key={index} />
                ))
              : bettieChatRooms?.map((chatRoom) => (
                  <BettieChatCard key={chatRoom.chatRoomId} chatRoom={chatRoom} />
                ))}
            {/* <SubscriptionUpgradeCard onClick={() => setIsSubscriptionBottomSheetOpen(true)} /> */}
            {isBettieBalancesLoading ||
            bettieBalances?.bettieChatRoomTicket?.balance === 0 ? null : (
              <BettieUnfinishedChatRoomCard
                balance={bettieBalances?.bettieChatRoomTicket?.balance ?? 0}
              />
            )}
          </div>
          {!isBettieBalancesLoading && !hasBettieChatRoomTicket && (
            <button
              type="button"
              onClick={handleAddChatRoomTicket}
              className="w-full mt-[10px] flex items-center justify-center gap-[6px] py-[19px] rounded-[18px] bg-pure-white dark:bg-background-elevated shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.08)] dark:shadow-[0_0_2px_0_rgba(0,0,0,0.83),_0_0_10px_0_rgba(0,0,0,0.40)]"
            >
              <Plus size={18} className="text-text-primary" />
              <p className="text-[16px] font-semibold leading-[19px] text-text-primary">
                {t('BettieChatList.startNewChat')}
              </p>
            </button>
          )}
          <p className="text-[12px] font-medium text-text-sub-lv1 leading-[16px] mt-[14px] text-center whitespace-pre-line">
            {t('BettieSetting.bettieDescription')}
          </p>
        </div>
      </div>
      {isCreateTicketBottomSheetOpen && (
        <BettieCreateRoomTicketBottomSheet
          isOpen={isCreateTicketBottomSheetOpen}
          onClose={() => setIsCreateTicketBottomSheetOpen(false)}
        />
      )}
      {isChargeBottomSheetOpen && (
        <BettieChargeBottomSheet
          isOpen={isChargeBottomSheetOpen}
          onClose={() => setIsChargeBottomSheetOpen(false)}
        />
      )}
      {isUnfinishedPopupOpen && (
        <UnfinishedPopup setIsUnfinishedPopupOpen={setIsUnfinishedPopupOpen} />
      )}
    </div>
  );
};

export default BettieChatListPage;

const BettieChatRoomCardSkeleton = () => {
  return (
    <div className="w-full rounded-[20px] bg-pure-white dark:bg-background-elevated shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_10px_0_rgba(0,0,0,0.08)] dark:shadow-[0_0_2px_0_rgba(0,0,0,0.83),_0_0_10px_0_rgba(0,0,0,0.40)] animate-pulse">
      <div className="flex items-center justify-between px-[24px] py-[14px] border-b border-surface-lv2">
        <div className="h-[14px] w-[70px] rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="h-[14px] w-[90px] rounded-full bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="flex items-center gap-[10px] px-[24px] py-[15px]">
        <div className="h-[46px] w-[46px] rounded-full bg-gray-100 dark:bg-gray-800" />

        <div className="flex flex-col gap-[4px] items-start flex-1">
          <div className="flex items-center gap-[4px] w-full">
            <div className="h-[18px] w-[80px] rounded-full bg-gray-100 dark:bg-gray-800" />
            <div className="h-[14px] w-[14px] rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="h-[16px] w-[70%] rounded-full bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </div>
  );
};

const UnfinishedPopup = ({
  setIsUnfinishedPopupOpen
}: {
  setIsUnfinishedPopupOpen: (isOpen: boolean) => void;
}) => {
  const navigate = useNavigate();
  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center">
      <PopUpBasic
        type="short"
        title={t('BettieChatList.haveUnfinishedChatRoom')}
        description={t('BettieChatList.selectAndChat')}
        primaryButtonText={t('no')}
        onPrimaryButtonClick={() => setIsUnfinishedPopupOpen(false)}
        secondaryButtonText={t('yes')}
        onSecondaryButtonClick={() => {
          setIsUnfinishedPopupOpen(false);
          navigate('/bettie-setting', { state: { initialStep: 'room_intro' } });
        }}
        className="w-[311px]"
      />
    </div>
  );
};

